/**
 * What a settings list edits (I-2510): one layer of the account config chain
 * — „Bearbeiten für". Every place that edits settings brings its layers as
 * scopes and the same list renders them all: the shell's settings page (the
 * profile, the appearances the session may edit, the session's company and
 * tenant for administrators), the look designer (the appearance it shows) and
 * the admin app's panels (a tenant, a company, an account).
 *
 * A scope's store reads the layer of each key — what the layer holds itself
 * over what the layers before it give (`AccountConfigLayerView`) — and writes
 * single fields of it. Where it reads from and how it writes is the scope's
 * business: the public door with the session's own layers, the admin door
 * for a tenant or company, the look designer's drafts.
 */
import type { Ref } from "vue";
import type {
  AccountConfigLayerRef,
  AccountConfigLayerView,
} from "../config/layerView";

/** The layer of one scope, key by key. */
export interface ConnectSettingsScopeStore {
  /** Reads the scope's layer of these keys (keys already read are kept). */
  load(keys: readonly string[]): Promise<void>;
  /**
   * The scope's layer of a key over the layers before it — with the changes
   * on their way applied. Reactive; null while the key is not read.
   */
  view(key: string): AccountConfigLayerView | null;
  /** Sets one field of the layer (`null` removes the layer's value). */
  set(key: string, path: readonly string[], value: unknown): void;
  /** Locks (or frees) a top-level field for every later layer — tenant and company. */
  lock?(key: string, field: string, locked: boolean): void;
  readonly loading: Readonly<Ref<boolean>>;
  /** The last failure in words (a refused write, a key that could not be read). */
  readonly error: Readonly<Ref<string | null>>;
}

/** An appearance a layer may select (`appearance.active`). */
export interface ConnectSettingsAppearanceOption {
  value: string;
  label: string;
}

/** One layer a settings list can edit. */
export interface ConnectSettingsScope {
  /** Unique among the list's scopes (`account:7`, `appearance:12`, `company:5`). */
  id: string;
  /** What „Bearbeiten für" shows: „Mein Profil", „Design „Atelier““, „Firma „BLP““. */
  label: string;
  /** The layer the scope edits. */
  target: AccountConfigLayerRef;
  /** Offer a lock per top-level field (tenant, company). */
  locking?: boolean;
  /** Show the fields only an administrator's layer sets. */
  adminLayer?: boolean;
  /** Read-only, and why (no permission). */
  readOnly?: string;
  /** Not to be picked, and why (a profile the session does not work in). */
  disabled?: string;
  /** Keys the layer cannot hold — an appearance holds no `appearance`. */
  excludedKeys?: readonly string[];
  /** The choices of `appearance` fields. */
  appearanceOptions?: Readonly<Ref<readonly ConnectSettingsAppearanceOption[]>>;
  /** The name of a layer where a caption needs one (an appearance's, a company's). */
  layerName?: (layer: AccountConfigLayerRef) => string | undefined;
  store: ConnectSettingsScopeStore;
}

/** What the shell hands an app's `settingsScopes`: where the session works. */
export interface ConnectSettingsScopeContext {
  tenant: { id: number; name: string } | null;
  company: { id: number; name: string } | null;
  /**
   * The shell's translation — it knows the libraries' texts (`settingsList.*`
   * of `@blpde/connect`), not the app's own: scope labels and errors that
   * show in the shell take those.
   */
  t: (key: string, named?: Record<string, unknown>) => string;
}

/** The shell's settings list (`/settings/list/<section>`). */
export const CONNECT_SETTINGS_LIST_PATH = "/settings/list";

/** Where the settings list opens a section (or its first one). */
export function connectSettingsListPath(section?: string): string {
  return section
    ? `${CONNECT_SETTINGS_LIST_PATH}/${encodeURIComponent(section)}`
    : CONNECT_SETTINGS_LIST_PATH;
}
